import { Store, Info, MapPin, Globe, Users, Phone, AlertTriangle, Target } from 'lucide-react';

export default function StoreInfoTab({ store, domainConfig }) {
    return (
        <div className="p-8 space-y-8">
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-50 to-slate-100/80 rounded-2xl p-6 border border-slate-200/60">
                <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                    <div className="p-2 bg-white rounded-xl shadow-sm border border-slate-200/60">
                        <Store size={24} className="text-slate-700" />
                    </div>
                    Store Information
                </h3>
                <p className="text-slate-600 mt-2 font-medium">Complete store profile and contact details</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Basic Information */}
                <div className="bg-white rounded-2xl p-8 border border-slate-200/60 shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
                            <Info size={24} className="text-blue-600" />
                        </div>
                        <h4 className="text-2xl font-bold text-slate-900">Basic Information</h4>
                    </div>
                    <div className="space-y-4">
                        <div className="bg-slate-50/80 rounded-xl p-4 border border-slate-200/60">
                            <div className="flex items-center gap-3 mb-2">
                                <Store size={16} className="text-slate-600" />
                                <span className="font-bold text-slate-700">Store Name</span>
                            </div>
                            <div className="text-lg font-bold text-slate-900">{store.name}</div>
                        </div>
                        <div className="bg-slate-50/80 rounded-xl p-4 border border-slate-200/60">
                            <div className="flex items-center gap-3 mb-2">
                                <MapPin size={16} className="text-slate-600" />
                                <span className="font-bold text-slate-700">Region</span>
                            </div>
                            <div className="text-lg font-bold text-slate-900">{store.region}</div>
                        </div>
                        {store.address && (
                            <div className="bg-slate-50/80 rounded-xl p-4 border border-slate-200/60">
                                <div className="flex items-center gap-3 mb-2">
                                    <MapPin size={16} className="text-slate-600" />
                                    <span className="font-bold text-slate-700">Address</span>
                                </div>
                                <div className="text-lg font-bold text-slate-900">{store.address}</div>
                            </div>
                        )}
                        {store.website && (
                            <div className="bg-slate-50/80 rounded-xl p-4 border border-slate-200/60">
                                <div className="flex items-center gap-3 mb-2">
                                    <Globe size={16} className="text-slate-600" />
                                    <span className="font-bold text-slate-700">Website</span>
                                </div>
                                <a href={store.website} target="_blank" rel="noreferrer"
                                    className="text-lg font-bold text-orange-600 hover:text-orange-700 hover:underline transition-colors">
                                    {store.website}
                                </a>
                            </div>
                        )}
                    </div>
                </div>

                {/* Contact Information */}
                <div className="bg-white rounded-2xl p-8 border border-slate-200/60 shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-3 bg-green-50 rounded-xl border border-green-200">
                            <Users size={24} className="text-green-600" />
                        </div>
                        <h4 className="text-2xl font-bold text-slate-900">Contact Information</h4>
                    </div>
                    <div className="space-y-4">
                        {store.contact?.name ? (
                            <div className="bg-slate-50/80 rounded-xl p-4 border border-slate-200/60">
                                <div className="flex items-center gap-3 mb-2">
                                    <Users size={16} className="text-slate-600" />
                                    <span className="font-bold text-slate-700">Contact Name</span>
                                </div>
                                <div className="text-lg font-bold text-slate-900">{store.contact.name}</div>
                            </div>
                        ) : (
                            <div className="bg-amber-50/80 rounded-xl p-4 border border-amber-200/60">
                                <div className="flex items-center gap-3 mb-2">
                                    <AlertTriangle size={16} className="text-amber-600" />
                                    <span className="font-bold text-amber-700">Contact Name</span>
                                </div>
                                <div className="text-sm text-amber-700 font-medium">Not available</div>
                            </div>
                        )}
                        {store.contact?.email ? (
                            <div className="bg-slate-50/80 rounded-xl p-4 border border-slate-200/60">
                                <div className="flex items-center gap-3 mb-2">
                                    <Users size={16} className="text-slate-600" />
                                    <span className="font-bold text-slate-700">Email</span>
                                </div>
                                <a href={`mailto:${store.contact.email}`}
                                    className="text-lg font-bold text-orange-600 hover:text-orange-700 hover:underline transition-colors">
                                    {store.contact.email}
                                </a>
                            </div>
                        ) : (
                            <div className="bg-amber-50/80 rounded-xl p-4 border border-amber-200/60">
                                <div className="flex items-center gap-3 mb-2">
                                    <AlertTriangle size={16} className="text-amber-600" />
                                    <span className="font-bold text-amber-700">Email</span>
                                </div>
                                <div className="text-sm text-amber-700 font-medium">Not available</div>
                            </div>
                        )}
                        {store.contact?.phone ? (
                            <div className="bg-slate-50/80 rounded-xl p-4 border border-slate-200/60">
                                <div className="flex items-center gap-3 mb-2">
                                    <Phone size={16} className="text-slate-600" />
                                    <span className="font-bold text-slate-700">Phone</span>
                                </div>
                                <div className="text-lg font-bold text-slate-900">{store.contact.phone}</div>
                            </div>
                        ) : (
                            <div className="bg-amber-50/80 rounded-xl p-4 border border-amber-200/60">
                                <div className="flex items-center gap-3 mb-2">
                                    <AlertTriangle size={16} className="text-amber-600" />
                                    <span className="font-bold text-amber-700">Phone</span>
                                </div>
                                <div className="text-sm text-amber-700 font-medium">Not available</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Page Statistics */}
            <div className="bg-white rounded-2xl p-8 border border-slate-200/60 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-purple-50 rounded-xl border border-purple-200">
                        <Target size={24} className="text-purple-600" />
                    </div>
                    <div>
                        <h4 className="text-2xl font-bold text-slate-900">Page Statistics</h4>
                        <p className="text-slate-600 font-medium">Breakdown of pages by domain</p>
                    </div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-2xl p-6 text-center border border-slate-200/60">
                        <div className="text-4xl font-bold text-slate-900 mb-2">{store.pages.length}</div>
                        <div className="text-sm font-bold text-slate-600">Total Pages</div>
                    </div>
                    {Object.entries(store.pages.reduce((acc, page) => {
                        acc[page.domain] = (acc[page.domain] || 0) + 1;
                        return acc;
                    }, {})).map(([domain, count]) => {
                        const config = domainConfig[domain];
                        return (
                            <div key={domain} className={`rounded-2xl p-6 text-center border-2 ${config.bgColor} ${config.borderColor}`}>
                                <div className={`text-4xl font-bold mb-2 ${config.color}`}>{count}</div>
                                <div className={`text-sm font-bold ${config.color}`}>{domain} Pages</div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
